import { ClientPrintJob, FileSourceType, InstalledPrinter, JSPrintManager, PrintFile, WSStatus } from 'jsprintmanager';
import html2canvas from 'html2canvas-pro';

/*
 * Тих печат през JSPrintManager — същото, което ползва Деливери
 * (apps/web/src/lib/jspm.ts там). Етикетите отиват направо на принтера
 * „etiketen", без прозорец за печат на браузъра — иначе той помни
 * последния принтер и етикет излизаше на А4 (01.10.2026).
 *
 * JSPM Client трябва да е инсталиран на компютъра (localhost:25443).
 * Ако го няма — печатът минава през обикновения прозорец, както преди.
 */

let startPromise = null;

function ensureStarted() {
  if (!startPromise) {
    JSPrintManager.auto_reconnect = true;
    startPromise = JSPrintManager.start().catch((err) => {
      console.warn('[JSPM] start() се провали:', err);
      return undefined;
    });
  }
  return startPromise;
}

/** Свързан ли е JSPM Client на този компютър (чака до `timeoutMs`). */
export async function isJspmAvailable(timeoutMs = 4000) {
  await Promise.race([ensureStarted(), new Promise((r) => setTimeout(r, timeoutMs))]);
  return JSPrintManager.websocket_status === WSStatus.Open;
}

/** Имената на инсталираните принтери — празно, ако JSPM го няма. */
export async function getInstalledPrinters() {
  if (!(await isJspmAvailable())) return [];
  const result = await JSPrintManager.getPrinters();
  return Array.isArray(result) ? result.filter((x) => typeof x === 'string' && x.trim()) : [];
}

/**
 * Хартиите, които принтерът познава. „label1" например на „etiketen" в
 * офиса НЯМА (има 51x26, 4 x 4…) — с несъществуваща хартия JSPM
 * отказваше тихо и не излизаше нищо (01.10.2026).
 */
export async function getPapers(printerName) {
  if (!(await isJspmAvailable())) return [];
  try {
    const res = await JSPrintManager.getPapers(printerName);
    const list = Array.isArray(res) ? res : (res?.papers ?? []);
    return list.filter((x) => typeof x === 'string' && x.trim());
  } catch { return []; }
}

/** Хартията за печат: исканата, ако я има; иначе 51x26; иначе първата налична. */
export async function izberiHartiya(printerName, iskana) {
  const papers = await getPapers(printerName);
  if (papers.length === 0) return iskana;
  if (papers.includes(iskana)) return iskana;
  if (papers.includes('51x26')) return '51x26';
  return papers.find((x) => x !== 'USER') ?? papers[0];
}

// 60 сек.: пробната версия чака клик „Click here to start Printing…".
const TIMEOUT_MS = 60000;

/**
 * Снима елементите (html2canvas) и ги праща като ЕДНО задание — по файл
 * на етикет, — за да има само един клик в пробната версия на JSPM.
 *
 * Изтекло време НЕ значи неуспешен печат: при нелицензиран клиент
 * отговорът понякога не идва, а заданието вече е излязло. Затова
 * повикващият НЕ бива да пада към прозореца на браузъра при грешка —
 * иначе печата два пъти (урокът от Деливери, 03.09.2026).
 */
export async function printElements(elements, printerName, iskanaHartiya) {
  const paperName = await izberiHartiya(printerName, iskanaHartiya);
  const printer = new InstalledPrinter(printerName);
  printer.paperName = paperName;
  const job = new ClientPrintJob();
  job.clientPrinter = printer;
  for (const [i, el] of elements.entries()) {
    const canvas = await html2canvas(el, { scale: 5, backgroundColor: '#ffffff' });
    const base64 = canvas.toDataURL('image/png').substring('data:image/png;base64,'.length);
    job.files.push(new PrintFile(base64, FileSourceType.Base64, `etiket-${i + 1}.png`, 1));
  }

  /*
   * Отговорът на клиента идва като резултат на sendToClient() — той се
   * изпълнява, когато заданието свърши, и се отхвърля с грешката на
   * клиента. „onFinished" в библиотеката НЯМА.
   */
  job.onUpdate = (data) => console.info('[JSPM] Статус:', data);
  job.onError = (data, isCritical) => console.warn('[JSPM] Грешка от клиента:', data, isCritical);
  let t;
  try {
    const res = await Promise.race([
      job.sendToClient(),
      new Promise((_, reject) => {
        t = setTimeout(() => reject(new Error('JSPrintManager не потвърди печата навреме.')), TIMEOUT_MS);
      }),
    ]);
    console.info('[JSPM] Готово:', res);
  } catch (err) {
    const text = typeof err === 'string' ? err : (err?.message ?? JSON.stringify(err));
    throw new Error(text.startsWith('JSPrintManager') ? text : `JSPrintManager отказа: ${text}`);
  } finally {
    clearTimeout(t);
  }
}
